import { useQuery } from '@tanstack/react-query'
import { Button, Form, Select, Typography } from 'antd'
import type { OrderContractSettings } from '@super-mega/server/contracts/order'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { CONTRACT_TEMPLATE_TYPES } from '@/features/contract/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { cn } from '@/utils/index.ts'

type ContractFormValues = {
  contractSettings?: OrderContractSettings
  items?: { contractSettings?: OrderContractSettings }[]
}

export function RentalContractFields({ service = false, itemName }: { service?: boolean; itemName?: number }) {
  const trpc = useTRPC()
  const templates = useQuery(trpc.order.contracts.options.queryOptions())
  const form = Form.useFormInstance<ContractFormValues>()
  const orderSettings = Form.useWatch('contractSettings', form)
  // 监听稳定的列表路径，删除前面的项目导致索引变化时仍读取该项目的合同。
  const items = Form.useWatch('items', form)
  const settings = itemName === undefined ? orderSettings : items?.[itemName]?.contractSettings
  return (
    <section
      className={cn(
        itemName === undefined
          ? 'border-border bg-surface scroll-mt-24 rounded-xl border p-5 shadow-xs'
          : 'border-border mt-4 border-t pt-4'
      )}
      id={itemName === undefined ? 'section-rental-contract' : undefined}
    >
      <Typography.Title className="mb-1!" level={5}>
        合同设置
      </Typography.Title>
      <Typography.Text type="secondary">一个订单可以配置多份合同，打印时可分别调整正文。</Typography.Text>
      <Form.List name={itemName === undefined ? 'contractSettings' : [itemName, 'contractSettings']}>
        {(fields, { add, remove }) => (
          <div className="mt-4 flex flex-col gap-3">
            {fields.map((field) => (
              <div key={field.key} className="grid items-start gap-3 sm:grid-cols-[1fr_2fr_auto]">
                <Form.Item name={[field.name, 'contractType']} label="合同类型" rules={[{ required: true }]}>
                  <Select
                    options={Object.entries(CONTRACT_TEMPLATE_TYPES)
                      .filter(([value]) =>
                        service ? ['service', 'supplement', 'other'].includes(value) : value !== 'service'
                      )
                      .map(([value, item]) => ({ value, label: item.label }))}
                    onChange={() =>
                      form.setFieldValue(
                        itemName === undefined
                          ? ['contractSettings', field.name, 'templateId']
                          : ['items', itemName, 'contractSettings', field.name, 'templateId'],
                        undefined
                      )
                    }
                  />
                </Form.Item>
                <Form.Item name={[field.name, 'templateId']} label="合同模板">
                  <Select
                    allowClear
                    loading={templates.isFetching}
                    placeholder="可在打印时选择模板"
                    options={(templates.data ?? [])
                      .filter((t) => t.type === settings?.[field.name]?.contractType)
                      .map((t) => ({ value: t.id, label: t.name }))}
                  />
                </Form.Item>
                <Authorized permissions={['order.rental.delete']}>
                  <Button className="sm:mt-7" danger onClick={() => remove(field.name)}>
                    移除
                  </Button>
                </Authorized>
              </div>
            ))}
            <Button
              type="dashed"
              disabled={fields.length >= 20 || undefined}
              onClick={() => add({ contractType: service ? 'service' : 'lease' })}
            >
              添加合同
            </Button>
          </div>
        )}
      </Form.List>
    </section>
  )
}
